import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Package, TrendingUp, Zap, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { useBundleAnalyzer } from '@/hooks/useBundleAnalyzer';

const BundleAnalyzer = () => {
  const { bundleStats, isAnalyzing, analyzeBundles, getSizeRecommendations, formatBytes } = useBundleAnalyzer();

  const getRecommendationIcon = (type: string) => {
    switch (type) {
      case 'error': return <AlertTriangle className="w-4 h-4 text-destructive" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'info': return <Info className="w-4 h-4 text-blue-600" />;
      default: return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
  };

  const getRecommendationVariant = (type: string) => {
    switch (type) {
      case 'error': return 'destructive' as const;
      case 'warning': return 'default' as const;
      default: return 'default' as const;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Bundle Analysis
            </CardTitle>
            <Button 
              onClick={analyzeBundles} 
              disabled={isAnalyzing}
              variant="outline"
            >
              {isAnalyzing ? 'Analyzing...' : 'Analyze Bundles'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!bundleStats && !isAnalyzing && (
            <div className="text-center py-8 text-muted-foreground">
              Click "Analyze Bundles" to start bundle size analysis
            </div>
          )}

          {isAnalyzing && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Analyzing bundle sizes...</p>
            </div>
          )}

          {bundleStats && (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{formatBytes(bundleStats.totalSize)}</div>
                  <div className="text-sm text-muted-foreground">Total Size</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{formatBytes(bundleStats.gzippedSize)}</div>
                  <div className="text-sm text-muted-foreground">Gzipped Size</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">
                    {Math.round((bundleStats.gzippedSize / bundleStats.totalSize) * 100)}%
                  </div>
                  <div className="text-sm text-muted-foreground">Compression Ratio</div>
                </div>
              </div>

              {/* Chunk Breakdown */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Chunk Breakdown
                </h3>
                <div className="space-y-3">
                  {bundleStats.chunks.map((chunk, index) => {
                    const percentage = (chunk.size / bundleStats.totalSize) * 100;
                    return (
                      <div key={index} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium capitalize">{chunk.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {formatBytes(chunk.size)}
                            </span>
                            <Badge variant="outline">{Math.round(percentage)}%</Badge>
                          </div>
                        </div>
                        <Progress value={percentage} className="h-2" />
                        <div className="text-xs text-muted-foreground">
                          Assets: {chunk.assets.join(', ')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Performance Metrics */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Performance Impact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 border rounded-lg">
                    <div className="font-medium">Load Time</div>
                    <div className="text-2xl font-bold text-primary">
                      {bundleStats.performance.loadTime}ms
                    </div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="font-medium">Initial Chunk</div>
                    <div className="text-lg font-semibold">
                      {formatBytes(bundleStats.performance.initialChunkSize)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <h3 className="font-semibold mb-3">Optimization Recommendations</h3>
                <div className="space-y-3">
                  {getSizeRecommendations().map((rec, index) => (
                    <Alert key={index} variant={getRecommendationVariant(rec.type)}>
                      <div className="flex items-start gap-2">
                        {getRecommendationIcon(rec.type)}
                        <div className="flex-1">
                          <AlertDescription className="font-medium">
                            {rec.message}
                          </AlertDescription>
                          <div className="text-sm text-muted-foreground mt-1">
                            {rec.action}
                          </div>
                        </div>
                      </div>
                    </Alert>
                  ))}
                  {getSizeRecommendations().length === 0 && (
                    <Alert>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <AlertDescription>
                        Bundle size is optimized! No recommendations at this time.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BundleAnalyzer;